import {
  useFindAllCompaniesQuery,
  useFindAllInventoryHardwareQuery,
  useFindAllSoftwareQuery,
  useFindAllSoftwareVersionsQuery,
  useFindOneDemosQuery,
  useRemoveDemoMutation,
} from '../../api/api-hooks';
import { Grid, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import {
  getISOStringOneMonthFromToday,
  truncateISOString,
} from '../../utils/dateTime';
import { DemosAddItemForm } from './demosTypes';
import useDemosItemCreator from './useDemosItemCreator';
import useDemosItemUpdater from './useDemosItemUpdater';
import { demosHandlerMiddleware, itemValidator } from './demosUtils';
import { useHardwareTypeMap } from '../../hooks/useHardwareTypeMap';
import SoftwareTypeManagementButton from '../software-types/SoftwareTypeManagementButton';

interface Props {
  selectedDemos: Item[];
  setSelectedDemos: React.Dispatch<React.SetStateAction<Item[]>>;
  editModalOpenFlag: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}
const addFormInitialState: DemosAddItemForm = {
  company: null,
  inventoryHardware: null,
  firmware: null,
  software: null,
  softwareVersion: null,
  contactName: null,
  shipDate: truncateISOString(new Date().toISOString()),
  startDate: truncateISOString(new Date().toISOString()),
  endDate: truncateISOString(getISOStringOneMonthFromToday()),
};

const DemosOptionsBar = ({
  selectedDemos,
  setSelectedDemos,
  editModalOpenFlag,
  setEditModalOpenFlag,
}: Props) => {
  const [deleteDemo] = useRemoveDemoMutation();
  const demoCreator = useDemosItemCreator();
  const demoUpdater = useDemosItemUpdater();
  const typeMap = useHardwareTypeMap();
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: InventoryHardware } = useFindAllInventoryHardwareQuery({});
  const { data: software } = useFindAllSoftwareQuery({});
  const { data: versions } = useFindAllSoftwareVersionsQuery({});
  if (
    companies === undefined ||
    InventoryHardware === undefined ||
    software === undefined ||
    versions === undefined ||
    typeMap === undefined
  )
    return null;
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedDemos}
        setSelectedItems={setSelectedDemos}
        handlerMiddleware={demosHandlerMiddleware}
        itemValidator={itemValidator}
        formInstructions='Company and Contact Name are required.'
        addFormInitialState={addFormInitialState}
        itemName='Demo'
        editModalOpen={editModalOpenFlag}
        setEditModalOpen={setEditModalOpenFlag}
        deleteNotes='Deleting this Demo will return any associated analyzers to Inventory.'
        childrenCreator={(addFormState: DemosAddItemForm, handler: any) => (
          <Grid container spacing={4}>
            <Grid container item xs={6} direction='column' spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'company');
                  }}
                  value={addFormState.company}
                  options={companies}
                  getOptionLabel={(company) => company.companyName}
                  style={{ width: 300, marginTop: '.5rem' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Company'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='contactName'
                  label='Contact Name'
                  fullWidth
                  value={nullStringCheck(addFormState.contactName)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Ship Date: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='shipDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.shipDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Demo Start: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='startDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.startDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Demo End: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='endDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.endDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container direction='column' item xs={6} spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'inventoryHardware');
                  }}
                  value={addFormState.inventoryHardware}
                  options={InventoryHardware}
                  getOptionLabel={(analyzer) => analyzer.serial}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Hardware'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Typography>
                  Hardware Type:{' '}
                  {addFormState.inventoryHardware
                    ? typeMap.get(addFormState.inventoryHardware.hardwareTypeId)
                    : 'None Selected'}
                </Typography>
              </Grid>
              <Grid item>
                <Typography color='error'>
                  Associating Hardware with a Demo removes the Hardware from
                  Inventory until the Demo is deleted or the Hardware is removed
                  from the Demo.
                </Typography>
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='firmware'
                  label='Firmware'
                  fullWidth
                  value={nullStringCheck(addFormState.firmware)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'software');
                  }}
                  value={addFormState.software}
                  options={software}
                  getOptionLabel={(software) => software.type}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Software Type'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <SoftwareTypeManagementButton />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'softwareVersion');
                  }}
                  value={addFormState.softwareVersion}
                  options={versions.filter(
                    (version) =>
                      version.softwareId === addFormState.software?.id
                  )}
                  getOptionLabel={(version) => version.versionNumber}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Software Version'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={demoCreator}
        itemGetter={useFindOneDemosQuery}
        itemUpdater={demoUpdater}
        itemDeleter={deleteDemo}
      />
    </>
  );
};

export default DemosOptionsBar;
